import React, { useState, useEffect, useMemo } from "react";
import TextInput from '../library/TextInput';
import API_BASE_URL from "../../config/api";

const SalaryData = ({ employeeId }) => {
  const [rows, setRows] = useState([]);
  const [notification, setNotification] = useState(null);
  const [generalMaster, setGeneralMaster] = useState([]);

  const itemOptions = {
    Allowance: ['Base Salary', 'Bonus', 'Benefits'],
    Deduction: ['Tax', 'Insurance', 'Loan'],
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/SalaryComponents/${employeeId}`)
      .then((res) => res.json())
      .then((data) => {
        setRows(data.map(row => ({ ...row, isEditing: false })));
      })
      .catch((err) => console.error(err));
  }, [employeeId]);
  

  useEffect(() => {

    const fetchgeneralMaster = async () => {
      const res = await fetch(`${API_BASE_URL}/api/Config/AllGeneralMaster`, {
        
      });
      if (res.ok) {
        const data = await res.json();
        setGeneralMaster(data);
      }
    };
    fetchgeneralMaster();
 
}, [employeeId]);

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = field === 'value' ? Number(value) : value;
    if (field === 'category' && itemOptions[value]) {
      updated[index].type  = itemOptions[value][0];
    }
    setRows(updated);
  };

  const handleDelete = async (index) => { 
    const row = rows[index];
    if (row.id) {
      await fetch(`${API_BASE_URL}/api/SalaryComponents/${row.id}`, { method: 'DELETE' });
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        id: 0,
        employeeId: employeeId,
        category: 60,
        type: itemOptions['Allowance'][0],
        value: '',
        isEditing: true,
      },
    ]);
  };

  const handleToggleEdit = (index) => {
    const updated = [...rows];
    const row = updated[index];

    if (row.isEditing) {
      // Validate before marking as done
      if (!row.type || !row.category || row.value === '') {
        setNotification({
          type: 'error',
          message: 'Please fill all required fields (Type, Item, Value)!.'
        });
        return;
      }
      if (isNaN(row.value) || Number(row.value) <= 0) {
        setNotification({
          type: 'error',
          message: 'Value must be a positive number.'
        });
        return;
      }

      const isDuplicate = rows.some(
        (r, i) =>
          i !== index &&
          r.category === row.category &&
          r.type === row.type 
      );
  
      if (isDuplicate) {
        setNotification({
          type: 'error',
          message: `This data already exists.`
        });
        return;
      }
    }
    handleCloseNotification();
    updated[index].isEditing = !row.isEditing;
    setRows(updated);
  };

  const handleSaveAll = async () => {
    const hasIncomplete = rows.some(
      (row) =>
        !row.category ||
        !row.type ||
        row.value === '' ||
      isNaN(row.value)
    );
    if (hasIncomplete) {
      setNotification({
        type: 'error',
        message: 'Please ensure all rows are complete and valid before saving.'
      });
      return;
    }

    const hasUnconfirmed = rows.some((row) => row.isEditing);
    if (hasUnconfirmed) {
      setNotification({
        type: 'error',
        message: 'Please confirm all rows (click Done) before saving.',
      });
      return;
    }

    const seen = new Set();
    const duplicate = rows.some((row) => {
      const key = `${row.category}-${row.type}`;
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
  
    if (duplicate) {
      setNotification({
        type: 'error',
        message: 'Duplicate Allowance/Deduction type found.',
      });
      return;
    }
  

    const payload = rows.map(({ isEditing, ...rest }) => ({
      ...rest,
      employeeId: employeeId,
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/SalaryComponents/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setNotification({ type: 'success', message: 'Salary Components saved successfully.' });
        // Refresh rows
        fetch(`${API_BASE_URL}/api/SalaryComponents/${employeeId}`)
          .then((res) => res.json())
          .then((data) =>
            setRows(data.map(row => ({ ...row, isEditing: false })))
          );
      } else {
        const error = await response.text();
        setNotification({ type: 'error', message: 'Error saving data: ' + error });
      }
    } catch (err) {
      setNotification({ type: 'error', message: 'Network error: ' + err.message });
    }
  };


  const handleCloseNotification = () => {
    setNotification(null);
  };

  const summary = useMemo(() => {
    let allowances = 0;
    let deductions = 0;
    rows.forEach(({ category, value }) => {
      const num = parseFloat(value) || 0;
      if (category === 60) allowances += num;
      else deductions += num;
    });
    return {
      allowances,
      deductions,
      net: allowances - deductions,
    };
  }, [rows]);

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold text-[#121516] pb-3">Salary Components</h1>
      {notification && (
        <div className={`mb-4 px-4 py-3 rounded flex justify-between items-start ${notification.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          }`}>
          <span>{notification.message}</span>
          <button onClick={handleCloseNotification} className="ml-4 font-bold text-lg leading-none">×</button>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-xl p-6 border border-[#dde1e3]">
          <p className="text-[#121516] text-base font-medium leading-normal">Allowances</p>
          <p className="text-[#121516] tracking-light text-2xl font-bold leading-tight">₹{summary.allowances.toLocaleString()}</p>
        </div>
        <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-xl p-6 border border-[#dde1e3]">
          <p className="text-[#121516] text-base font-medium leading-normal">Deductions</p>
          <p className="text-[#121516] tracking-light text-2xl font-bold leading-tight">
          ₹{summary.deductions.toLocaleString()}
          </p>
        </div>
        <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-xl p-6 border border-[#dde1e3]">
          <p className="text-[#121516] text-base font-medium leading-normal">Net Salary</p>
          <p className="text-[#121516] tracking-light text-2xl font-bold leading-tight">
          ₹{summary.net.toLocaleString()}
          </p>
        </div>
      </div>


      <div className="grid p-2 grid-cols-[200px_250px_180px_150px] gap-4 items-center bg-white border border-[#dde1e3] rounded-xl p-4 mt-4">
        <div>Type</div>
        <div>Item</div>
        <div>Value</div>
        <div>Actions</div>

        {rows.map((row, index) => (
          <React.Fragment key={row.id || index}>
                <select
          value={row.category}
          onChange={(e) => handleChange(index, 'category', e.target.value)}
          className="form-input"
          disabled={!row.isEditing}
        >
          <option value="">Select </option>
          {generalMaster.filter(x => x.mainId == 59).map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.title}
            </option>
          ))}
        </select>

            <select
          value={row.type}
          onChange={(e) => handleChange(index, 'type', e.target.value)}
          className="form-input"
          disabled={!row.isEditing}
        >
          <option value="">Select </option>
          {generalMaster.filter(x => x.mainId == row.category).map((opt) => (
            <option  key={opt.id} value={opt.id}>
              {opt.title}
            </option>
          ))}
        </select>

            <TextInput
              type="number"
              value={row.value}
              className={row.isEditing ? "input-editing" : "input-readonly"}
              onChange={(e) => handleChange(index, 'value', e.target.value)}
              disabled={!row.isEditing}
            />

            <div className="flex gap-2 text-blue-600">
              <button onClick={() => handleToggleEdit(index)}>
                {row.isEditing ? 'Done' : 'Edit'}
              </button>
              <button className='text-red-600' onClick={() => handleDelete(index)}>Delete</button>
            </div>
          </React.Fragment>
        ))}
        <div className="col-span-4 pt-4">
          <button
            className="bg-[#f0f3f4] text-[#111518] px-4 py-2 rounded text-sm font-medium"
            onClick={handleAddRow}
          >
            + Add Row
          </button>
        </div>

      </div>
      <div className="flex flex-wrap  px-4  py-4 justify-end">
        <button onClick={handleSaveAll}
          className="custom-btn"
        >
          <span className="truncate">Save</span>
        </button>
      </div>


    </div>
  );
};

export default SalaryData;
